import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MagnifyingGlassIcon, XMarkIcon, UserIcon } from '@heroicons/react/24/outline';

interface HeaderProps {
  isTransparent?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isTransparent = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [isMenuLocked, setIsMenuLocked] = useState(true); // 초기값 true로 시작
  const [clickedMenuId, setClickedMenuId] = useState<number | null>(() => {
    const saved = sessionStorage.getItem('clickedMenuId');
    return saved ? parseInt(saved, 10) : null;
  });
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Close mobile menu and reset expanded state when menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      setExpandedMobileMenu(null);
    }
  }, [isMenuOpen]);

  // 페이지 이동 시 메뉴 잠금 (마우스가 헤더 밖으로 나갔다가 다시 들어와야 열림)
  useEffect(() => {
    setIsHeaderHovered(false);
    setIsMenuLocked(true);
  }, [location.pathname]);

  // Scroll detection for transparent header
  useEffect(() => {
    if (!isTransparent) return;

    const handleScroll = () => {
      const scrollThreshold = window.innerHeight * 0.8;
      setIsScrolled(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isTransparent]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchKeyword.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchKeyword.trim())}`);
      setSearchKeyword('');
    }
  };

  const menuItems = [
    {
      id: 1,
      title: '센터소개',
      path: '/about/greeting',
      subItems: [
        { title: '인사말', path: '/about/greeting' },
        { title: 'DGTC소개', path: '/about/gtc' },
        { title: '강사 소개', path: '/about/instructors' },
        { title: '교육과정', path: '/curriculum' },
        { title: '시설안내', path: '/about/facilities' },
        { title: '오시는 길', path: '/about/location' }
      ]
    },
    {
      id: 2,
      title: '프로그램',
      path: '/programs',
      subItems: [
        { title: '단체체험', path: '/programs/category/group' },
        { title: '유치과정', path: '/programs/category/preschool' },
        { title: '초등과정', path: '/programs/category/elementary' },
        { title: '중·고등과정', path: '/programs/category/secondary' },
        { title: '성인과정', path: '/programs/category/adult' },
        { title: '특별과정', path: '/programs/category/special' },
        { title: '방학캠프', path: '/programs/category/camp' },
        { title: '국제교류', path: '/programs/category/international' }
      ]
    },
    {
      id: 3,
      title: '공지사항',
      path: '/notice',
      subItems: [
        { title: '공지사항', path: '/notice' },
        // { title: '센터 NEWS', path: '/notice/news' },
        { title: '교육 자료실', path: '/notice/materials' },
        { title: '보도자료', path: '/notice/press' }
      ]
    },
    {
      id: 4,
      title: '수강신청',
      path: '/application',
      subItems: [
        { title: '수강신청', path: '/application/register' },
        { title: '센터 일정안내', path: '/application/schedule' },
        { title: '단체체험(기관) 신청', path: '/application/group' }
      ]
    },
    {
      id: 5,
      title: 'LMS',
      path: '/lms',
      subItems: [
        { title: '레벨테스트', path: '/lms/level-test' },
        { title: '학습 허브', path: '/lms/learning-hub' }
      ]
    },
    {
      id: 6,
      title: '참여마당',
      path: '/community',
      subItems: [
        { title: '자주 묻는 질문', path: '/community/faq' },
        { title: '갤러리', path: '/gallery' }
        // { title: '수강후기', path: '/community/reviews' },
        // { title: '설문조사', path: '/community/survey' }
      ]
    }
  ];

  // 투명 모드 여부 (스크롤/호버 상태 반영)
  const isInTransparentMode = isTransparent && !isHeaderHovered && !isScrolled;

  return (
    <>
      {/* 상단 유틸리티 바 */}
      <div className={`fixed w-full top-0 z-[101] transition-colors duration-300 ${
        isInTransparentMode ? 'bg-black/20' : 'bg-[#eeeeee] border-b border-gray-200'
      }`}>
        <div className="w-full px-2 md:px-4 2xl:px-6">
          <div className="flex justify-end items-center h-11 gap-2">
            {/* 검색 - 돋보기 아이콘 또는 검색창 */}
            {!isSearchOpen ? (
              <button
                onClick={() => setIsSearchOpen(true)}
                className={`p-2 transition-colors ${
                  isInTransparentMode ? 'text-white hover:text-blue-200' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
            ) : (
              <form onSubmit={handleSearch} className="flex items-center">
                <div className={`flex items-center rounded-full px-3 py-1 ${
                  isInTransparentMode
                    ? 'bg-white/20 border border-white/30'
                    : 'bg-white border border-gray-300'
                }`}>
                  <input
                    type="text"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    className={`bg-transparent outline-none text-sm w-40 md:w-52 ${
                      isInTransparentMode ? 'text-white placeholder-white/70' : 'text-gray-700 placeholder-gray-400'
                    }`}
                    placeholder="검색어를 입력하세요"
                    autoFocus
                  />
                  <button type="submit" className={isInTransparentMode ? 'text-white' : 'text-gray-500'}>
                    <MagnifyingGlassIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchKeyword('');
                    }}
                    className={`ml-1 ${isInTransparentMode ? 'text-white' : 'text-gray-500'}`}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </form>
            )}

            {/* 로그인/회원가입/마이페이지 */}
            <div className="hidden md:flex items-center gap-1 text-sm">
              {!user ? (
                <>
                  <Link
                    to="/login"
                    className={`px-3 py-1 transition-colors ${
                      isInTransparentMode ? 'text-white hover:text-blue-200' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    로그인
                  </Link>
                  <span className={isInTransparentMode ? 'text-white/50' : 'text-gray-300'}>|</span>
                  <Link
                    to="/signup"
                    className={`px-3 py-1 transition-colors ${
                      isInTransparentMode ? 'text-white hover:text-blue-200' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    회원가입
                  </Link>
                </>
              ) : (
                <>
                  <span className={isInTransparentMode ? 'text-white' : 'text-gray-700'}>
                    {user.name}님
                  </span>
                  <span className={isInTransparentMode ? 'text-white/50' : 'text-gray-300'}>|</span>
                  {user.user_type === 'admin' ? (
                    <Link
                      to="/admin"
                      className={`px-3 py-1 transition-colors ${
                        isInTransparentMode ? 'text-white hover:text-blue-200' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      관리자
                    </Link>
                  ) : (
                    <Link
                      to="/mypage"
                      className={`px-3 py-1 transition-colors ${
                        isInTransparentMode ? 'text-white hover:text-blue-200' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      마이페이지
                    </Link>
                  )}
                  <span className={isInTransparentMode ? 'text-white/50' : 'text-gray-300'}>|</span>
                  <button
                    onClick={handleLogout}
                    className={`px-3 py-1 transition-colors ${
                      isInTransparentMode ? 'text-white hover:text-blue-200' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    로그아웃
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 메인 헤더 (Nav) */}
      <header
        ref={headerRef}
        className={`header fixed w-full top-11 z-[100] transition-colors duration-300 ${
          isInTransparentMode ? 'bg-transparent' : 'bg-white border-b border-gray-200'
        }`}
        onMouseEnter={() => {
          if (!isMenuLocked) {
            setIsHeaderHovered(true);
          }
        }}
        onMouseLeave={() => {
          setIsHeaderHovered(false);
          setIsMenuLocked(false);
          setClickedMenuId(null);
          sessionStorage.removeItem('clickedMenuId');
        }}
      >
        <div className="header__inner w-full relative">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex justify-between items-center h-16">
              {/* Logo - absolute 포지션으로 왼쪽 고정 */}
              <Link
                to="/"
                className="header__logo absolute left-6 md:left-10 2xl:left-16 top-1/2 -translate-y-1/2 flex items-center group z-[110]"
              >
                <img
                  src="/logo-full.png"
                  alt="동두천시 글로벌인재교육센터"
                  className="h-7 sm:h-8 md:h-10 w-auto transition-all duration-300 group-hover:scale-105"
                />
              </Link>

              {/* Mobile Menu Button - absolute 포지션으로 오른쪽 고정 */}
              <button
                className="2xl:hidden header__btn absolute right-4 md:right-6 top-1/2 -translate-y-1/2"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <div className="btn__wrap w-6 h-6 flex flex-col justify-center space-y-1.5">
                  {isMenuOpen ? (
                    <XMarkIcon className={`h-6 w-6 transition-colors duration-300 ${
                      isInTransparentMode ? 'text-white' : 'text-gray-700'
                    }`} />
                  ) : (
                    <>
                      <span className={`block w-full h-0.5 transition-colors duration-300 ${
                        isInTransparentMode ? 'bg-white' : 'bg-gray-700'
                      }`}></span>
                      <span className={`block w-full h-0.5 transition-colors duration-300 ${
                        isInTransparentMode ? 'bg-white' : 'bg-gray-700'
                      }`}></span>
                      <span className={`block w-full h-0.5 transition-colors duration-300 ${
                        isInTransparentMode ? 'bg-white' : 'bg-gray-700'
                      }`}></span>
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Desktop Navigation - 중앙에서 약간 우측 배치 */}
          <nav className="hidden 2xl:block absolute left-1/2 transform -translate-x-[45%] top-0 h-16 pointer-events-auto z-[100]">
            <ul className="flex m-0 p-0 list-none h-16 gap-6">
              {menuItems.map((item) => (
                <li
                  key={item.id}
                  className="m-0 p-0 flex items-center justify-center"
                  style={{ width: '120px' }}
                  onMouseEnter={() => {
                    if (isMenuLocked && clickedMenuId !== null && clickedMenuId !== item.id) {
                      setIsMenuLocked(false);
                      setIsHeaderHovered(true);
                      setClickedMenuId(null);
                      sessionStorage.removeItem('clickedMenuId');
                    }
                  }}
                >
                  <Link
                    to={item.path}
                    className={`font-semibold text-[18px] transition-colors duration-300 whitespace-nowrap ${
                      isInTransparentMode
                        ? 'text-white hover:text-blue-300'
                        : 'text-gray-700 hover:text-blue-600'
                    }`}
                    onClick={() => {
                      setIsHeaderHovered(false);
                      setIsMenuLocked(true);
                      setClickedMenuId(item.id);
                      sessionStorage.setItem('clickedMenuId', item.id.toString());
                    }}
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Desktop Dropdown Background - Full Width (Only for 2xl and above) */}
          <div className={`hidden 2xl:block absolute w-screen bg-white border-t border-gray-200 shadow-lg overflow-hidden z-[90] transition-all duration-300`}
            style={{
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              height: isHeaderHovered ? '450px' : '0',
              paddingTop: isHeaderHovered ? '30px' : '0',
              paddingBottom: isHeaderHovered ? '80px' : '0',
              opacity: isHeaderHovered ? 1 : 0,
              visibility: isHeaderHovered ? 'visible' : 'hidden'
            }}
          >
            <div className="w-full flex justify-center">
              <div className="transform translate-x-[5%]">
                <ul className="flex m-0 p-0 list-none gap-6">
                  {menuItems.map((item) => (
                    <li key={item.id} className="m-0 p-0" style={{ width: '120px' }}>
                      <ul className="m-0 p-0 list-none">
                        {item.subItems.map((subItem) => (
                          <li key={subItem.path} className="m-0 p-0 mb-7 last:mb-0">
                            <Link
                              to={subItem.path}
                              className="block text-[16px] font-medium text-gray-600 hover:text-blue-600 transition-colors whitespace-nowrap text-center"
                              onClick={() => {
                                setIsHeaderHovered(false);
                                setIsMenuLocked(true);
                                setClickedMenuId(item.id);
                                sessionStorage.setItem('clickedMenuId', item.id.toString());
                              }}
                            >
                              {subItem.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      <div
        className={`2xl:hidden fixed left-0 right-0 bg-white border-b border-gray-200 shadow-lg overflow-hidden transition-all duration-300 z-[90]`}
        style={{
          top: '108px', /* 44px (utility bar) + 64px (header) */
          maxHeight: isMenuOpen ? '80vh' : '0',
          opacity: isMenuOpen ? 1 : 0
        }}
      >
        <nav className="overflow-y-auto" style={{ maxHeight: '80vh' }}>
          {/* 모바일 로그인/회원가입 버튼 */}
          <div className="border-b border-gray-100 bg-gray-50">
            {!user ? (
              <div className="flex gap-2 px-6 py-4">
                <button
                  onClick={() => {
                    navigate('/login');
                    setIsMenuOpen(false);
                  }}
                  className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                >
                  로그인
                </button>
                <button
                  onClick={() => {
                    navigate('/signup');
                    setIsMenuOpen(false);
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                >
                  회원가입
                </button>
              </div>
            ) : (
              <div className="px-6 py-4">
                <div className="text-sm text-gray-600 mb-3">{user.name}님</div>
                <div className="flex flex-col gap-2">
                  {user.user_type !== 'admin' && (
                    <button
                      onClick={() => {
                        navigate('/mypage');
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      <UserIcon className="h-5 w-5" />
                      마이페이지
                    </button>
                  )}
                  <div className="flex gap-2">
                    {user.user_type === 'admin' && (
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setIsMenuOpen(false);
                        }}
                        className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                      >
                        관리자
                      </button>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                    >
                      로그아웃
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {menuItems.map((item) => (
            <div key={item.id} className="border-b border-gray-100">
              <button
                onClick={() => setExpandedMobileMenu(expandedMobileMenu === item.id ? null : item.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-gray-700 font-medium text-base hover:bg-gray-50"
              >
                <span>{item.title}</span>
                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${
                    expandedMobileMenu === item.id ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 bg-gray-50`}
                style={{
                  maxHeight: expandedMobileMenu === item.id ? `${item.subItems.length * 48}px` : '0'
                }}
              >
                {item.subItems.map((subItem) => (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className="block px-6 py-3 text-gray-600 text-sm hover:text-blue-600 hover:bg-gray-100"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setExpandedMobileMenu(null);
                    }}
                  >
                    {subItem.title}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div
          className="2xl:hidden fixed inset-0 bg-black bg-opacity-20 z-[80]"
          style={{ top: '108px' }}
          onClick={() => {
            setIsMenuOpen(false);
            setExpandedMobileMenu(null);
          }}
        />
      )}
    </>
  );
};

export default Header;
